import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import { APP_API_V1_BASE } from "../../../config/api";

const API = APP_API_V1_BASE;

export default function CreateLocation({
  open,
  onClose,
  onSuccess,
  locationData = null,
}) {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    radius: "",
  });

  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(locationData);

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      latitude: "",
      longitude: "",
      radius: "",
    });
  };

  useEffect(() => {
    if (!open) return;

    if (locationData) {
      setFormData({
        name: locationData.name ?? "",
        address: locationData.address ?? "",
        latitude: locationData.latitude ?? "",
        longitude: locationData.longitude ?? "",
        radius: locationData.radius ?? "",
      });
    } else {
      resetForm();
    }
  }, [open, locationData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (loading) return;

    if (
      !formData.name.trim() ||
      !formData.address.trim() ||
      formData.latitude === "" ||
      formData.longitude === "" ||
      formData.radius === ""
    ) {
      alert("All fields are required.");
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      const payload = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        radius: Number(formData.radius),
      };

      if (isEdit) {
        await axios.put(
          `${API}/attendance/location/${locationData.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        await axios.post(`${API}/attendance/location`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      resetForm();

      onSuccess?.();

      onClose();
    } catch (error) {
      console.error(
        `${isEdit ? "Update" : "Create"} Location Error`,
        error
      );

      alert(
        error.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;

    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background:
              "linear-gradient(135deg, rgba(15,23,42,.96), rgba(15,118,110,.88) 55%, rgba(8,47,73,.92))",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            color="white"
          >
            {isEdit ? "Update Location" : "Create Location"}
          </Typography>

          <IconButton
            onClick={handleClose}
            sx={{ color: "#fff" }}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 2,
            mt: 1,
          }}
        >
          <TextField
            label="Location Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Radius (Meters)"
            name="radius"
            type="number"
            value={formData.radius}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Latitude"
            name="latitude"
            type="number"
            value={formData.latitude}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Longitude"
            name="longitude"
            type="number"
            value={formData.longitude}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Address"
            name="address"
            multiline
            rows={3}
            value={formData.address}
            onChange={handleChange}
            fullWidth
            required
            sx={{
              gridColumn: {
                xs: "span 1",
                sm: "span 2",
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress
                size={18}
                color="inherit"
                sx={{ mr: 1 }}
              />
              {isEdit ? "Updating..." : "Creating..."}
            </>
          ) : isEdit ? (
            "Update Location"
          ) : (
            "Create Location"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}